<template>
  <div class="operating-information">
    <accordion-panel
      id="operating-information"
      :collapsed.sync="collapsed"
      @shown="onShownAccordion"
    >
      <template #title>
        {{ $t('engineering.cooling-maintenance.operating-information') }}
      </template>
      <s-empty-list
        v-if="showEmptyList"
        :loading="loading"
      />
      <div
        v-else
        class="operating-information__charts-wrapper"
      >
        <b-row class="mb-3">
          <b-col cols="12" md="6">
            <div class="operating-information-chart avoid-break">
              <div class="font-weight-bold py-2">
                {{ $t('engineering.cooling-maintenance.time-since-last-maintenance') }}
              </div>
              <e-chart
                class="operating-information-chart__chart"
                autoresize
                ref="sinceTimeChart"
                @legendselectchanged="changeLegend"
                @rendered="onRendered($event, 'sinceTimeChart')"
              />
              <img
                class="operating-information-chart__image w-100 avoid-break"
                alt="operating-information-chart-image"
                :src="chartImg.sinceTimeChart"
              >
            </div>
          </b-col>
          <b-col cols="12" md="6">
            <div class="operating-information-chart avoid-break">
              <div class="font-weight-bold py-2">
                {{ $t('engineering.cooling-maintenance.total-time') }}
              </div>
              <e-chart
                class="operating-information-chart__chart"
                autoresize
                ref="totalTimeChart"
                @rendered="onRendered($event, 'totalTimeChart')"
              />
              <img
                class="operating-information-chart__image w-100 avoid-break"
                alt="operating-information-chart-image"
                :src="chartImg.totalTimeChart"
              >
            </div>
          </b-col>
        </b-row>
        <div class="line-chart__legend mb-3">
          <chart-legends
            v-show="chartData.length"
            :items="legend"
            :chart-id="'operating-information'"
            :hidden-legends="hiddenLegends"
            :items-unique-property="itemsUniqueProperty"
            :colors-list="colorsList"
            @toggle-legend="toggleLegend"
            @select-all-legends="selectAllLegends"
            @unselect-all-legends="unselectAllLegends"
          />
        </div>
      </div>
    </accordion-panel>

  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import uniqBy from 'lodash/uniqBy';
import { CoolingMaintenanceOperationInformationChart as ChartTypes } from 'sigma-mappings';
import themeService from '@/services/themeService';
import { getColorsListByLength } from '@/domain/data/charts/chartColors';
import AccordionPanel from '@/components/AccordionPanel';
import defaultChartMixin from '@/components/mixins/defaultChartMixin';
import ChartLegends from '@/components/charts/ChartLegends';

export default {
  name: 'OperatingInformationSection',
  components: {
    AccordionPanel,
    ChartLegends,
  },
  props: {
    loading: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    collapsed: false,
    chartsRefs: ['sinceTimeChart', 'totalTimeChart'],
    hiddenLegends: [],
    chartImg: {
      sinceTimeChart: '#',
      totalTimeChart: '#',
    },
  }),
  mixins: [defaultChartMixin],
  computed: {
    ...mapGetters(['isPrinting']),
    showEmptyList() {
      return this.loading || !this.chartData?.length;
    },
    sinceTimeChartData() {
      return this.getMappedChartData(ChartTypes.TIME_SINCE_LAST_MAINTENANCE);
    },
    sinceTimeChartOptions() {
      return this.getOptions(this.sinceTimeChartData);
    },
    totalTimeChartData() {
      return this.getMappedChartData(ChartTypes.TOTAL_TIME);
    },
    totalTimeChartOptions() {
      return this.getOptions(this.totalTimeChartData);
    },
    colorsList() {
      const colorsAmount = (this.sinceTimeChartData || []).length;
      return getColorsListByLength(colorsAmount);
    },
    legend() {
      return uniqBy(this.sinceTimeChartData, this.itemsUniqueProperty);
    },
  },
  mounted() {
    this.setChartOptions();
  },
  methods: {
    getMappedChartData(type) {
      const [chart] = (this.chartData || []).filter(item => item.chart === type);
      if (!chart) return [];
      return (chart.data || []).map(item => ({
        name: item.description,
        values: [item.value],
      }));
    },
    getSizeByValue(chartData = []) {
      const MULTIPLIER = 10;
      const SAFE_MARGIN = 20;
      const max = chartData.map(item => item.values[0])
        .reduce((a, b) => Math.max(a, b), []);
      return (max.toString().length * MULTIPLIER) + SAFE_MARGIN;
    },
    getOptions(chartData) {
      const leftGridSize = this.getSizeByValue(chartData);

      return {
        legend: {
          show: false,
        },
        backgroundColor: this.isPrinting
          ? themeService.getStyleVar('chart-print-background')
          : themeService.getStyleVar('color-background-dark'),
        xAxis: {
          type: 'category',
          data: [],
          axisLine: {
            lineStyle: {
              color: this.fontColor,
            },
          },
        },
        yAxis: {
          type: 'value',
          splitNumber: 3,
          splitLine: {
            show: false,
          },
          axisLine: {
            show: true,
            lineStyle: {
              color: this.fontColor,
            },
          },
        },
        grid: {
          top: 30,
          left: leftGridSize,
          bottom: 20,
          right: 20,
        },
        barCategoryGap: leftGridSize,
        series: chartData.map((item, index) => ({
          name: item.name,
          label: {
            show: true,
            position: 'top',
          },
          data: item.values,
          barGap: 0,
          barMinHeight: 5,
          type: 'bar',
          itemStyle: {
            borderColor: '#fff',
            borderWidth: 1,
            color: this.colorsList[index],
          },
        })),
      };
    },
    async setChartOptions() {
      await this.$nextTick();
      const replaceMerge = { replaceMerge: ['yAxis', 'series'] };
      if (this.$refs.sinceTimeChart) {
        this.$refs.sinceTimeChart
          .delegateMethod('setOption', this.sinceTimeChartOptions, replaceMerge);
      }
      if (this.$refs.totalTimeChart) {
        this.$refs.totalTimeChart
          .delegateMethod('setOption', this.totalTimeChartOptions, replaceMerge);
      }
    },
    onRendered($event, chartType) {
      this.$nextTick(() => {
        this.chartImg[chartType] = !!this.collapsed || !this.$refs[chartType]?.height
          ? '#'
          : this.$refs[chartType].getDataURL();
      });
    },
    onShownAccordion() {
      this.hiddenLegends = [];
      this.setChartOptions();
    },
  },
  watch: {
    chartData() {
      this.setChartOptions();
    },
    isPrinting() {
      this.setChartOptions();
    },
  },
};
</script>

<style lang="sass" scoped>
@import "src/styles/defaults/variables"

.operating-information
  border: 1px solid $color-border-gray
  border-radius: 5px

  ::v-deep
    .accordion-panel
      box-shadow: none

      &__header
        padding: 2px 15px !important
        font-weight: bold

  .operating-information-chart

    &__chart
      border: 1px solid var(--color-border-dark)
      border-radius: 5px
      min-height: 200px
      max-height: 260px
      width: 100%

    &__image
      display: none

    @media print
      .operating-information-chart
        &__chart
          display: none

        &__image
          display: block
</style>
